import analyticsAction from "../analytics/analytics-action"

export default function navigation(el: HTMLElement) {
  const nav = el.querySelector<HTMLElement>("nav")
  const alertBanner = el.querySelector<HTMLDivElement>(".alert-banner")
  const menuToggle = el.querySelector<HTMLButtonElement>("#main-menu-toggle")
  const mainMenu = el.querySelector<HTMLDivElement>("div#main-menu")
  const mobileSearchToggle = el.querySelector<HTMLButtonElement>(
    "#mobile-search-toggle"
  )
  const desktopSearchToggle = el.querySelector<HTMLButtonElement>(
    "#submenu-toggle-search"
  )
  const mobileSearchMenu = el.querySelector<HTMLDivElement>("div#mobile-search")
  const subMenuToggles = mainMenu?.querySelectorAll<HTMLButtonElement>(
    "button.sub-menu-toggle[aria-expanded]"
  )
  const mainMenuUnderlay = el.querySelector<HTMLDivElement>(
    "div#main-menu-underlay"
  )

  if (
    !menuToggle ||
    !mainMenu ||
    !nav ||
    !mobileSearchToggle ||
    !mobileSearchMenu ||
    !desktopSearchToggle
  ) {
    return
  }

  // The menu is open if menu toggle has aria-expanded="true" or
  // if submenu toggles have aria-expanded="true" and it is desktop
  const desktopMql = window.matchMedia("(min-width: 1200px)")

  const handleMenu = () => {
    const isMenuOpen =
      menuToggle.getAttribute("aria-expanded") === "true" ||
      mobileSearchToggle.getAttribute("aria-expanded") === "true" ||
      (desktopMql.matches &&
        !!mainMenu.querySelector("button.sub-menu-toggle[aria-expanded=true]"))

    if (isMenuOpen) {
      document.body.classList.add("relative", "overflow-hidden", "h-full")
    } else {
      document.body.classList.remove("relative", "overflow-hidden", "h-full")
    }
  }

  const closeMenu = () => {
    mobileSearchToggle.setAttribute("aria-expanded", "false")
    menuToggle.setAttribute("aria-expanded", "false")
    subMenuToggles?.forEach(subMenuToggle => {
      subMenuToggle.setAttribute("aria-expanded", "false")
    })
  }

  const setAlertOffset = () => {
    const mqlOffset = desktopMql.matches ? 1 : 0
    const alertOffset = alertBanner
      ? `${alertBanner.offsetHeight - alertBanner.getBoundingClientRect().bottom + mqlOffset}px`
      : "0px"
    document.body.style.setProperty("--alert-offset", alertOffset)
  }

  const toggleMutationObserver = new MutationObserver(mutationList => {
    mutationList.forEach(mutation => {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "aria-expanded"
      ) {
        const targetEl = mutation.target as HTMLElement
        const drawerExpanded = targetEl.getAttribute("aria-expanded") === "true"
        const labelFor = targetEl === menuToggle ? "menu" : "search"

        targetEl.setAttribute(
          "aria-label",
          drawerExpanded ? `Close ${labelFor}` : `Open ${labelFor}`
        )

        if (
          targetEl === mobileSearchToggle ||
          targetEl === desktopSearchToggle
        ) {
          handleSearchToggle(targetEl, drawerExpanded)
        }
      }
    })
  })

  const handleSearchToggle = (targetEl: HTMLElement, isExpanded: boolean) => {
    if (isExpanded) {
      analyticsAction("Search opened")
    }
  }

  const toggleAriaExpanded = (toggle: HTMLButtonElement) => {
    toggle.setAttribute(
      "aria-expanded",
      toggle.getAttribute("aria-expanded") === "true" ? "false" : "true"
    )
  }

  const setHeights = () => {
    const navHeight = `${nav.offsetHeight}px`
    const alertHeight = alertBanner ? `${alertBanner.offsetHeight}px` : "0px"
    document.body.style.setProperty("--nav-height", navHeight)
    document.body.style.setProperty("--alert-height", alertHeight)
  }

  const mastheadResizeObserver = new ResizeObserver(() => {
    setHeights()
  })

  // Event listeners and observers
  mastheadResizeObserver.observe(el)
  toggleMutationObserver.observe(menuToggle, { attributes: true })
  toggleMutationObserver.observe(mobileSearchToggle, { attributes: true })
  toggleMutationObserver.observe(desktopSearchToggle, { attributes: true })

  subMenuToggles?.forEach(subMenuToggle => {
    subMenuToggle.addEventListener("click", () => {
      subMenuToggles.forEach(toggle => {
        toggle !== subMenuToggle &&
          toggle.setAttribute("aria-expanded", "false")
      })
      toggleAriaExpanded(subMenuToggle)
      setAlertOffset()
      handleMenu()
    })
  })

  menuToggle.addEventListener("click", () => {
    toggleAriaExpanded(menuToggle)
    setAlertOffset()
    if (menuToggle.getAttribute("aria-expanded") === "false") {
      subMenuToggles?.forEach(subMenuToggle => {
        subMenuToggle.setAttribute("aria-expanded", "false")
      })
    } else {
      mobileSearchToggle.setAttribute("aria-expanded", "false")
    }
    handleMenu()
  })

  mobileSearchToggle.addEventListener("click", () => {
    toggleAriaExpanded(mobileSearchToggle)
    if (mobileSearchToggle.getAttribute("aria-expanded") === "true") {
      menuToggle.setAttribute("aria-expanded", "false")
      subMenuToggles?.forEach(subMenuToggle => {
        subMenuToggle.setAttribute("aria-expanded", "false")
      })
    }
    setAlertOffset()
    handleMenu()
  })

  mainMenu.addEventListener("click", e => {
    e.stopPropagation()
  })

  mainMenuUnderlay?.addEventListener("click", () => {
    closeMenu()
    handleMenu()
  })

  document.addEventListener("keydown", e => {
    if (e.key === "Escape") {
      closeMenu()
      handleMenu()
    }
  })

  desktopMql.addEventListener("change", e => {
    closeMenu()
    handleMenu()
  })

  // Mobile menu book demo button
  const mobileDemoButton = document.querySelector(".mobile-demo-button")
  let scrollpos = window.scrollY
  window.addEventListener("scroll", function () {
    scrollpos = window.scrollY
    if (mobileDemoButton) {
      if (scrollpos <= 600) {
        mobileDemoButton.classList.add("hidden")
        mobileDemoButton.classList.remove("block")
      } else {
        mobileDemoButton.classList.add("block")
        mobileDemoButton.classList.remove("hidden")
      }
    }
  })

  // Add shadow to nav on scroll
  // let scrollpos = window.scrollY
  // const header = document.querySelector("nav")
  // const header_height = header ? header.offsetHeight : 0
  // const add_class_on_scroll = () =>
  //   header?.classList.add("shadow-2", "bg-white")
  // const remove_class_on_scroll = () =>
  //   header?.classList.remove("shadow-2", "bg-white")
  // window.addEventListener("scroll", function () {
  //   scrollpos = window.scrollY
  //   if (scrollpos >= header_height) {
  //     add_class_on_scroll()
  //   } else {
  //     remove_class_on_scroll()
  //   }
  // })

  // Main
  setHeights()
}
